import {
  makeFetchSlice,
  fetchEdgeDataThunk,
  fetchDataThunk,
} from 'app/redux-fetch'
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppDispatch } from 'app/store'
import {
  LICENSES_COLLAPSE_STATE_REDUCER_NAME,
  IMPORT_LICENSE_REDUCER_NAME,
  LICENSES_LIST_REDUCER_NAME,
  LICENSES_NOTE_UPDATE_REDUCER_NAME,
  RECLAIM_LICENSE_REDUCER_NAME,
  TRANSFER_LICENSE_REDUCER_NAME,
  UPDATE_LICENSES_REDUCER_NAME,
  LICENSES_CODE_TYPES,
} from './consts'
import {
  fetchLicenses,
  FetchLicensesParams,
  importLicense,
  ImportLicensePayload,
  reclaimLicense,
  transferLicense,
  updateLicenseNote,
} from './api'
import {
  LicenesCollapseState,
  LicensesCollapseStateActionPayload,
  LicenseType,
} from './types'

export const updateLicenses = createAction<{ newLicense: LicenseType }>(
  UPDATE_LICENSES_REDUCER_NAME
)

const licensesListSlice = makeFetchSlice(
  LICENSES_LIST_REDUCER_NAME,
  (builder) => {
    builder.addCase(updateLicenses, (state, action) => {
      const { newLicense } = action.payload
      const { data: licenses } = state

      if (!Array.isArray(licenses)) {
        state.data = [newLicense]
      }

      state.data = licenses.map((license: LicenseType) => {
        if (license.id === newLicense.id) {
          return {
            ...newLicense,
            homeName: license.homeName,
            homeRootDomainId: license.homeRootDomainId,
          }
        }

        return license
      })
    })
  }
)

export const licensesListReducer = licensesListSlice.reducer
export const licensesListActions = licensesListSlice.actions

export const fetchLicensesListThunk = (
  domainId: string,
  params: FetchLicensesParams
) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchEdgeDataThunk({
        dispatch,
        actions: licensesListActions,
        asyncFunc: () => fetchLicenses(domainId, params),
      })
    } catch (error) {
      console.log('Error fetching licenses', error)
    }
  }
}

const updateLicenseNoteSlice = makeFetchSlice(LICENSES_NOTE_UPDATE_REDUCER_NAME)
export const updateLicenseNoteReducer = updateLicenseNoteSlice.reducer
export const updateLicenseNoteActions = updateLicenseNoteSlice.actions

export const updateLicenseNoteThunk = (
  domainId: string,
  licenseId: number,
  note: string
) => {
  return async (dispatch: AppDispatch) =>
    await fetchEdgeDataThunk({
      dispatch,
      actions: updateLicenseNoteActions,
      asyncFunc: () => updateLicenseNote(domainId, licenseId, { notes: note }),
    })
}

const importLicenseSlice = makeFetchSlice(IMPORT_LICENSE_REDUCER_NAME)
export const importLicenseReducer = importLicenseSlice.reducer
export const importLicenseActions = importLicenseSlice.actions

export const importLicenseThunk = ({
  domainId,
  code,
}: ImportLicensePayload) => {
  return async (dispatch: AppDispatch) =>
    await fetchDataThunk({
      dispatch,
      actions: importLicenseActions,
      asyncFunc: () => importLicense({ domainId, code }),
    })
}

const reclaimLicenseSlice = makeFetchSlice(RECLAIM_LICENSE_REDUCER_NAME)
export const reclaimLicenseReducer = reclaimLicenseSlice.reducer
export const reclaimLicenseActions = reclaimLicenseSlice.actions

export const reclaimLicenseThunk = (
  domainId: string,
  licenseId: number,
  rootDomainId: string
) => {
  return async (dispatch: AppDispatch) => {
    const updatedLicense = await fetchEdgeDataThunk({
      dispatch,
      actions: reclaimLicenseActions,
      asyncFunc: () => reclaimLicense({ domainId, licenseId, rootDomainId }),
    })

    await dispatch(updateLicenses({ newLicense: updatedLicense }))
  }
}

const transferLicenseSlice = makeFetchSlice(TRANSFER_LICENSE_REDUCER_NAME)
export const transferLicenseReducer = transferLicenseSlice.reducer
export const transferLicenseActions = transferLicenseSlice.actions

export const transferLicenseThunk = (
  purchaserDomainId: string,
  licenseId: number,
  targetId: string
) => {
  return async (dispatch: AppDispatch) => {
    const updatedLicense = await fetchEdgeDataThunk({
      dispatch,
      actions: transferLicenseActions,
      asyncFunc: () =>
        transferLicense(purchaserDomainId, licenseId, { id: targetId }),
    })

    await dispatch(updateLicenses({ newLicense: updatedLicense }))
  }
}
const licenesesCollapseStateSlice = createSlice({
  name: LICENSES_COLLAPSE_STATE_REDUCER_NAME,
  initialState: {
    [LICENSES_CODE_TYPES.available]: [],
    [LICENSES_CODE_TYPES.used]: [],
  } as LicenesCollapseState,
  reducers: {
    updateLicenesesCollapseState: (
      state,
      {
        payload: { groupName, showAvailableCodes },
      }: PayloadAction<LicensesCollapseStateActionPayload>
    ) => {
      const licensesCodesType = showAvailableCodes
        ? LICENSES_CODE_TYPES.available
        : LICENSES_CODE_TYPES.used
      return {
        ...state,
        [licensesCodesType]: state[licensesCodesType].includes(groupName)
          ? state[licensesCodesType].filter((e) => e !== groupName)
          : [...state[licensesCodesType], groupName],
      }
    },
  },
})

export const licenesCollapseStateReducer = licenesesCollapseStateSlice.reducer
export const { updateLicenesesCollapseState } =
  licenesesCollapseStateSlice.actions
